import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Chip,
    TextField,
    Alert,
    Paper,
    Autocomplete
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import LockIcon from '@mui/icons-material/Lock';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { getDecodedToken } from 'service/service';
import { getDropDownData } from 'service/unifiedAlerts';
import { checkRole } from 'utils/roleUtils';
import LoaderComponent from 'components/Loader';
import { PaginatedResultsTable } from './PaginatedResultsTable';

import { SectionHeader, StyledTextField, StyledSelect, inputBorderStyles, labelStyle, ewsAutocompletePaperSx } from './SharedComponents';
import { ConfigureRuleFormState, ResourceOption, RuleTestSampleResult, HandlerOption, ConfigureRulesTabProps } from './types';
import {
    RESOURCE_TYPE_OPTIONS,
    initialConfigureRuleForm,
    SAVE_SIMULATED_DELAY_MS,
    initialHandlerOptions,
    SCHEDULE_UNIT_OPTIONS
} from './constants';
import { getRiskColor, validateScheduleValue, parseScheduleValue, formatScheduleValue, ensureQueryVariables, balanceJsonBrackets, mapDtoListToSavedRules } from './helpers';
import { saveRuleConfig, updateRuleConfig, getRuleConfigs, getEwsResources, runRuleTest, RuleConfigPayload, RuleTestPayload } from 'service/approvalWorkflow';

const OVERRIDE_AUTHORIZED_ROLES = ['Data Operations Admin'];

const ENVIRONMENT_OPTIONS: HandlerOption[] = [
    { value: 'uat', label: 'UAT' },
    { value: 'dev', label: 'DEV' },
    { value: 'prod', label: 'PROD' }
];

interface ScheduleErrors {
    evalWindow?: string;
    checkInterval?: string;
    coolDown?: string;
}

interface AuditLogEntry {
    timestamp: string;
    user: string;
    action: string;
}

const RuleTestItemRow: React.FC<{
    title: string;
    description: string;
    status: 'pass' | 'warning' | 'fail' | 'pending';
    onClick?: () => void;
}> = ({ title, description, status, onClick }) => {
    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1.5,
                px: 2,
                borderBottom: '1px solid #f1f5f9',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': onClick ? { backgroundColor: '#f8fafc' } : undefined,
                '&:last-child': { borderBottom: 'none' }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
                {status === 'pass' && <CheckCircleOutlineIcon sx={{ color: '#2e7d32', fontSize: '1.25rem' }} />}
                {status === 'warning' && <WarningAmberIcon sx={{ color: '#ed6c02', fontSize: '1.25rem' }} />}
                {status === 'fail' && <ErrorOutlineIcon sx={{ color: '#d32f2f', fontSize: '1.25rem' }} />}
                {status === 'pending' && <HourglassEmptyIcon sx={{ color: '#94a3b8', fontSize: '1.25rem' }} />}
                <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#1e293b' }}>
                        {title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {description}
                    </Typography>
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {onClick && (
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#39ACE7' }}>
                        View results &rarr;
                    </Typography>
                )}
                <Typography sx={{
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: status === 'pass' ? '#2e7d32' : status === 'warning' ? '#ed6c02' : status === 'fail' ? '#d32f2f' : '#94a3b8',
                    letterSpacing: '0.5px'
                }}>
                    {status === 'pass' ? 'PASS' : status === 'warning' ? 'WARN' : status === 'fail' ? 'FAIL' : 'PENDING'}
                </Typography>
            </Box>
        </Box>
    );
};

const ConfigureRulesTab: React.FC<ConfigureRulesTabProps> = ({
    ruleDetails,
    savedRules = [],
    mappings = [],
    selectedRuleId,
    onSelectRuleId,
    onNavigateToServiceMapping,
    onNavigateToTab,
    onCanProceedChange,
    onRuleConfigSaved,
    onChangeRule
}) => {
    const [controller] = usePepwiseAuthController();
    const { role } = controller;
    const isAuthorizedToOverride = checkRole(role, OVERRIDE_AUTHORIZED_ROLES);
    const decodedUser = useMemo(() => getDecodedToken(), []);
    const currentUser = useMemo(() => {
        return decodedUser ? decodedUser.email : 'unknown user';
    }, [decodedUser]);

    const userSector = useMemo(() => {
        return decodedUser?.sector?.name || localStorage.getItem('defaultSector') || 'North America';
    }, [decodedUser]);

    // Active rule selection for header card
    const [currentSelectedId, setCurrentSelectedId] = useState<number | undefined>(() => {
        if (selectedRuleId !== undefined) return selectedRuleId;
        if (savedRules.length > 0) return savedRules[savedRules.length - 1].id;
        return undefined;
    });

    useEffect(() => {
        if (selectedRuleId !== undefined) {
            setCurrentSelectedId(selectedRuleId);
        } else if (savedRules.length > 0 && currentSelectedId === undefined) {
            setCurrentSelectedId(savedRules[savedRules.length - 1].id);
        }
    }, [selectedRuleId, savedRules, currentSelectedId]);

    const handleSelectRule = (idStr: string) => {
        const id = Number(idStr);
        setCurrentSelectedId(id);
        onSelectRuleId?.(id);
        setTestExecuted(false);
        setSampleTestResults([]);
    };

    const activeSavedRule = useMemo(() => {
        if (savedRules.length === 0) return undefined;
        if (currentSelectedId !== undefined) {
            const matched = savedRules.find((r) => r.id === currentSelectedId);
            if (matched) return matched;
        }
        return savedRules[savedRules.length - 1];
    }, [savedRules, currentSelectedId]);

    const activeMapping = useMemo(() => {
        if (!activeSavedRule) return undefined;
        return mappings.find((m) => m.ewsRule.toLowerCase() === activeSavedRule.ruleName.toLowerCase() || m.id === activeSavedRule.id);
    }, [mappings, activeSavedRule]);

    const displayedRuleName = activeSavedRule?.ruleName || ruleDetails?.ruleName || 'Test Rule';
    const displayedStatus = activeSavedRule?.status || ruleDetails?.status || 'draft';
    const displayedRiskLevel = (activeSavedRule?.riskLevel || ruleDetails?.riskLevel || 'critical').toUpperCase();
    const displayedSignal = activeSavedRule?.ewsSignalName || ruleDetails?.signal || 'EWS.Volume.Drift';
    const displayedDomainPath = activeSavedRule
        ? `${activeSavedRule.domain || activeSavedRule.metric || 'Volume'} > ${activeSavedRule.component || activeSavedRule.dataSourceLabel || 'Ingestion'} > ${activeSavedRule.layer || 'Bronze / All'}`
        : (ruleDetails?.domainComponentLayer || 'Volume > Ingestion > Bronze / All');

    const displayedHandler = useMemo(() => {
        if (activeMapping?.handler) {
            const matchedOpt = initialHandlerOptions.find((o) => o.value.toLowerCase() === activeMapping.handler.toLowerCase());
            return matchedOpt ? matchedOpt.label : activeMapping.handler;
        }
        if (activeSavedRule?.handler) return activeSavedRule.handler;
        if (ruleDetails?.handlerAgent && ruleDetails.handlerAgent !== '—') return ruleDetails.handlerAgent;
        return '—';
    }, [activeMapping, activeSavedRule, ruleDetails]);

    const currentRuleDetails = useMemo(() => ({
        ruleName: displayedRuleName,
        signal: displayedSignal,
        domain: activeSavedRule?.domain || 'Volume',
        component: activeSavedRule?.component || 'adf',
        layer: activeSavedRule?.layer || 'bronze',
        domainComponentLayer: displayedDomainPath,
        riskLevel: displayedRiskLevel,
        status: displayedStatus,
        metricKPI: activeSavedRule?.metricKPI || ruleDetails?.metricKPI || 'Volume',
        dataSource: activeSavedRule?.dataSourceLabel || ruleDetails?.dataSource || 'Ingestion Metrics',
        detectionLogic: activeSavedRule?.detectionLogic || ruleDetails?.detectionLogic || '',
        handlerAgent: displayedHandler,
        notes: activeSavedRule?.notes || ruleDetails?.notes || '',
        createdAt: ruleDetails?.createdAt || new Date().toLocaleString()
    }), [displayedRuleName, displayedSignal, displayedDomainPath, displayedRiskLevel, displayedStatus, activeSavedRule, ruleDetails, displayedHandler]);

    // Condition details derived from detection logic
    const conditionInfo = useMemo(() => {
        const rawLogic = currentRuleDetails.detectionLogic || '';
        const valMatch = rawLogic.match(/(\d+(?:\.\d+)?)/);
        const value = valMatch ? valMatch[1] : '10';
        const hasDeviation = rawLogic.toLowerCase().includes('drift') || rawLogic.toLowerCase().includes('deviat');
        const operator = hasDeviation ? 'deviates by ±' : (rawLogic.includes('>=') ? '>=' : rawLogic.includes('<=') ? '<=' : rawLogic.includes('>') ? '>' : rawLogic.includes('<') ? '<' : 'deviates by ±');
        const unit = rawLogic.includes('%') ? '%' : (rawLogic.toLowerCase().includes('ms') ? 'ms' : '%');
        const metricName = currentRuleDetails.metricKPI || 'Record Count';
        return { metricName, operator, value, unit };
    }, [currentRuleDetails.detectionLogic, currentRuleDetails.metricKPI]);

    // Live Dropdowns fetched from real APIs
    const [dynamicSectorOptions, setDynamicSectorOptions] = useState<HandlerOption[]>([]);
    const [dynamicProgramOptions, setDynamicProgramOptions] = useState<HandlerOption[]>([]);
    const [dynamicDomainOptions, setDynamicDomainOptions] = useState<HandlerOption[]>([]);
    const dynamicResourceTypeOptions: HandlerOption[] = RESOURCE_TYPE_OPTIONS;
    const [dynamicResourceOptions, setDynamicResourceOptions] = useState<ResourceOption[]>([]);
    const [isLoadingResources, setIsLoadingResources] = useState<boolean>(false);

    const [form, setForm] = useState<ConfigureRuleFormState>(() => ({
        ...initialConfigureRuleForm,
        resourceType: initialConfigureRuleForm.resourceType || 'adf',
        sector: userSector ? userSector.toLowerCase().replace(/\s+/g, '-') : ''
    }));

    const [scheduleTouched, setScheduleTouched] = useState<Record<string, boolean>>({});
    const [sampleTestResults, setSampleTestResults] = useState<RuleTestSampleResult[]>([]);
    const [isRunningTest, setIsRunningTest] = useState(false);
    const [testExecuted, setTestExecuted] = useState(false);
    const [testFailed, setTestFailed] = useState(false);
    const [testErrorMessage, setTestErrorMessage] = useState('');
    const [dryRunMessage, setDryRunMessage] = useState('');
    const [dryRunFailed, setDryRunFailed] = useState(false);
    const [dryRunRawResults, setDryRunRawResults] = useState<Record<string, any>[]>([]);
    const [showDryRunTable, setShowDryRunTable] = useState(false);
    const [shareWithApprovers, setShareWithApprovers] = useState(false);
    const [overrideEnabled, setOverrideEnabled] = useState(false);
    const [, setAuditLog] = useState<AuditLogEntry[]>([]);

    // Fetch platform dropdowns from live backend APIs
    useEffect(() => {
        let isMounted = true;

        const loadPlatformDropdowns = async () => {
            try {
                const data = await getDropDownData();
                if (isMounted && data) {
                    if (Array.isArray(data.sector) && data.sector.length > 0) {
                        const sOpts = data.sector.map((s: string) => ({
                            value: s.toLowerCase().replace(/\s+/g, '-'),
                            label: s
                        }));
                        setDynamicSectorOptions(sOpts);
                        if (!form.sector && sOpts.length > 0) {
                            setForm((prev) => ({ ...prev, sector: sOpts[0].value }));
                        }
                    }
                    if (Array.isArray(data.programs) && data.programs.length > 0) {
                        const pOpts = data.programs.map((p: string) => ({
                            value: p.toLowerCase().replace(/\s+/g, '-'),
                            label: p
                        }));
                        setDynamicProgramOptions(pOpts);
                        if (!form.program && pOpts.length > 0) {
                            setForm((prev) => ({ ...prev, program: pOpts[0].value }));
                        }
                    }
                    if (Array.isArray(data.domains) && data.domains.length > 0) {
                        const dOpts = data.domains.map((d: string) => ({
                            value: d.toLowerCase().replace(/\s+/g, '-'),
                            label: d
                        }));
                        setDynamicDomainOptions(dOpts);
                        if (!form.businessDomain && dOpts.length > 0) {
                            setForm((prev) => ({ ...prev, businessDomain: dOpts[0].value }));
                        }
                    }
                }
            } catch (err) {
                console.log('Platform dropdowns API fetch note:', err);
            }
        };

        loadPlatformDropdowns();

        return () => { isMounted = false; };
    }, []);

    // Dynamically fetch live resources from Backend when Resource Type changes
    useEffect(() => {
        let isMounted = true;
        const currentType = (form.resourceType || 'adf').toUpperCase();

        const fetchResources = async () => {
            setIsLoadingResources(true);
            try {
                const resp = await getEwsResources(currentType);
                const rawList = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
                if (isMounted) {
                    const mapped: ResourceOption[] = rawList
                        .filter((item: any) => item !== null && item !== undefined && item !== '')
                        .map((item: any) => {
                            const name = typeof item === 'string' ? item : (item.name || item.value || String(item));
                            return {
                                value: name,
                                label: `${name} (${currentType})`,
                                resourceType: (form.resourceType || 'adf').toLowerCase()
                            };
                        });
                    setDynamicResourceOptions(mapped);
                }
            } catch (err) {
                console.log('EWS resources API fetch note:', err);
                if (isMounted) {
                    setDynamicResourceOptions([]);
                }
            } finally {
                if (isMounted) {
                    setIsLoadingResources(false);
                }
            }
        };

        fetchResources();

        return () => { isMounted = false; };
    }, [form.resourceType]);

    // Synchronize form values and test status whenever the active rule changes
    useEffect(() => {
        if (activeSavedRule) {
            const rawResources = activeSavedRule.resources || (activeSavedRule as any)?.Resources || [];
            const mappedResources = Array.isArray(rawResources)
                ? rawResources.map((r: any) => (typeof r === 'string' ? r : r.name || r.identifier || '')).filter(Boolean)
                : [];
            const ruleResourceType = (
                (Array.isArray(rawResources) && rawResources[0]?.type)
                    ? rawResources[0].type
                    : activeSavedRule.layer || 'adf'
            ).toLowerCase();

            const scheduleData = activeSavedRule.schedule || (activeSavedRule as any)?.Schedule;
            const evalWin = scheduleData?.Eval_Window || scheduleData?.evalWindow || '14d';
            const intervalVal = scheduleData?.Interval || scheduleData?.checkInterval || '1h';
            const cooldownVal = scheduleData?.Cooldown || scheduleData?.coolDown || '30m';

            setForm((prev) => ({
                ...prev,
                resourceType: (RESOURCE_TYPE_OPTIONS.some((o) => o.value.toLowerCase() === ruleResourceType.toLowerCase()) ? ruleResourceType : 'adf') as any,
                resources: mappedResources,
                evalWindow: evalWin,
                checkInterval: intervalVal,
                coolDown: cooldownVal
            }));

            const isPassed = activeSavedRule.Test_Run_Status === 'PASSED' || (activeSavedRule as any)?.testRunStatus === 'PASSED';
            setTestExecuted(isPassed);
            setTestFailed(false);
            setDryRunFailed(false);
            setDryRunMessage(isPassed ? 'No anomalies in last 24h' : '');
            setSampleTestResults([]);
            setTestErrorMessage('');
        } else {
            setForm((prev) => ({
                ...prev,
                resources: [],
                evalWindow: '14d',
                checkInterval: '1h',
                coolDown: '30m'
            }));
            setTestExecuted(false);
            setTestFailed(false);
            setDryRunFailed(false);
            setDryRunMessage('');
            setSampleTestResults([]);
            setTestErrorMessage('');
        }
    }, [activeSavedRule?.id]);

    const logAuditEntry = (action: string) => {
        setAuditLog((prev) => [{ timestamp: new Date().toLocaleString(), user: currentUser, action }, ...prev]);
    };

    const handleFormChange = (key: keyof ConfigureRuleFormState, value: string) => {
        setForm((prev) => {
            if (key === 'resourceType') {
                return {
                    ...prev,
                    resourceType: value as any,
                    resources: []
                };
            }
            return {
                ...prev,
                [key]: value
            };
        });
    };

    const scheduleErrors: ScheduleErrors = {
        evalWindow: validateScheduleValue('Eval Window', form.evalWindow),
        checkInterval: validateScheduleValue('Check Interval', form.checkInterval),
        coolDown: validateScheduleValue('Cool Down', form.coolDown)
    };
    const isScheduleValid = !scheduleErrors.evalWindow && !scheduleErrors.checkInterval && !scheduleErrors.coolDown;

    const evalParsed = useMemo(() => parseScheduleValue(form.evalWindow, 'd'), [form.evalWindow]);
    const checkParsed = useMemo(() => parseScheduleValue(form.checkInterval, 'h'), [form.checkInterval]);
    const coolDownParsed = useMemo(() => parseScheduleValue(form.coolDown, 'm'), [form.coolDown]);

    const handleScheduleNumChange = (field: 'evalWindow' | 'checkInterval' | 'coolDown', num: string) => {
        const defaultUnit = field === 'evalWindow' ? 'd' : (field === 'checkInterval' ? 'h' : 'm');
        const parsed = parseScheduleValue(form[field], defaultUnit);
        const formatted = formatScheduleValue(num, parsed.unit);
        setForm((prev) => ({ ...prev, [field]: formatted }));
    };

    const handleScheduleUnitChange = (field: 'evalWindow' | 'checkInterval' | 'coolDown', unit: string) => {
        const defaultUnit = field === 'evalWindow' ? 'd' : (field === 'checkInterval' ? 'h' : 'm');
        const parsed = parseScheduleValue(form[field], defaultUnit);
        const formatted = formatScheduleValue(parsed.num, unit);
        setForm((prev) => ({ ...prev, [field]: formatted }));
    };

    const handleScheduleBlur = (key: 'evalWindow' | 'checkInterval' | 'coolDown') => {
        setScheduleTouched((prev) => ({ ...prev, [key]: true }));
    };

    const conditionsCount = useMemo(() => {
        if (activeSavedRule?.orGroups && activeSavedRule.orGroups.length > 0) {
            return activeSavedRule.orGroups.flatMap((g) => g.rules).length;
        }
        if (activeSavedRule?.variables && activeSavedRule.variables.length > 0) {
            return activeSavedRule.variables.length;
        }
        return 1;
    }, [activeSavedRule]);

    const availableResourceOptions = useMemo(() => {
        const currentType = (form.resourceType || dynamicResourceTypeOptions[0]?.value || 'adf').toLowerCase();
        return dynamicResourceOptions.filter((r) => r.resourceType === currentType);
    }, [form.resourceType, dynamicResourceOptions, dynamicResourceTypeOptions]);

    const selectedResourceObjects = useMemo(() => {
        return dynamicResourceOptions.filter((opt) => form.resources.includes(opt.value));
    }, [form.resources, dynamicResourceOptions]);

    const handleRunTest = async () => {
        setIsRunningTest(true);
        setTestFailed(false);
        setTestErrorMessage('');

        if (form.resources.length === 0) {
            setTestFailed(true);
            setTestErrorMessage('Please select at least one resource in Resources / Pipelines before running the test.');
            setIsRunningTest(false);
            return;
        }

        if (!isScheduleValid) {
            setTestFailed(true);
            setTestErrorMessage('Rule test execution failed: Schedule configuration validation error.');
            setIsRunningTest(false);
            return;
        }

        setTestExecuted(true);

        // 1. Persist RuleConfig to Backend via standard ruleConfig API
        try {
            const rulePayload: RuleConfigPayload = {
                Domain: activeSavedRule?.domain || ruleDetails?.domain || 'Volume',
                Component: activeSavedRule?.component || ruleDetails?.component || 'adf',
                Layer_Tool: activeSavedRule?.layer || ruleDetails?.layer || 'Bronze / All',
                Metric_KPI: currentRuleDetails.metricKPI,
                EWS_Signal_Name: currentRuleDetails.signal,
                Detection_Logic_Trend_Pattern: currentRuleDetails.detectionLogic,
                Risk_Level: (currentRuleDetails.riskLevel?.toUpperCase() as any) || 'MEDIUM',
                Data_Source: currentRuleDetails.dataSource,
                Rule_Name: currentRuleDetails.ruleName,
                Notes: currentRuleDetails.notes,
                Handler_Type: 'pepwise',
                Handler: currentRuleDetails.handlerAgent || 'pepwise_db',
                Is_Deleted: false,
                Is_Active: false,
                Is_Approved: false,
                Schedule: {
                    Eval_Window: form.evalWindow,
                    Interval: form.checkInterval,
                    Cooldown: form.coolDown
                },
                Resources: form.resources.map((resName) => {
                    const matched = dynamicResourceOptions.find((r) => r.value === resName);
                    return {
                        type: (matched?.resourceType || form.resourceType || 'ADF').toUpperCase(),
                        name: resName,
                        identifier: resName
                    };
                }),
                Final_ES_Query: (() => {
                    const rawEs = activeSavedRule?.baseEsQuery || ruleDetails?.baseEsQuery;
                    let indexName = (rawEs && typeof rawEs === 'object' && rawEs.indexName) ? rawEs.indexName : 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_dev';
                    indexName = typeof indexName === 'string' ? indexName.replace(/^'|'$/g, '').replace(/^"|"$/g, '').trim() : indexName;
                    let query = (rawEs && typeof rawEs === 'object' && rawEs.query) ? rawEs.query : (typeof rawEs === 'string' ? rawEs : '{"query":{"match_all":{}}}');
                    query = balanceJsonBrackets(query);
                    const vars = (rawEs && typeof rawEs === 'object' && Array.isArray(rawEs.variables)) ? rawEs.variables : [];
                    const keys = (rawEs && typeof rawEs === 'object' && Array.isArray(rawEs.keysToExtract)) ? rawEs.keysToExtract : [];
                    const resolvedVars = ensureQueryVariables(query, vars, activeSavedRule?.variables || ruleDetails?.variables || []);

                    return {
                        indexName: indexName || 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_dev',
                        query,
                        Variables: resolvedVars.map((v: any) => ({
                            variableName: v.variableName || v.variableIdentifier || v.key || '',
                            variableIdentifier: v.variableIdentifier || v.variableName || v.key || '',
                            value: v.value,
                            ...(v.operator ? { operator: v.operator } : {})
                        })),
                        keysToExtract: keys
                    };
                })(),
                Variables: [],
                variables: [],
                Approval_Id: activeSavedRule?.approvalId || (activeSavedRule as any)?.Approval_Id || (activeSavedRule as any)?.approval_id || null,
                Workflow_Id: activeSavedRule?.workflowId || (activeSavedRule as any)?.Workflow_Id || (activeSavedRule as any)?.workflow_id || null,
                Test_Run_Status: 'PASSED'
            };

            const targetBackendId = activeSavedRule?.backendId || ruleDetails?.backendId;
            if (targetBackendId) {
                updateRuleConfig(targetBackendId, rulePayload).then((res) => {
                    if (res?.data) onRuleConfigSaved?.(res.data);
                }).catch((err) => console.error('Update rule config error:', err));
            } else {
                saveRuleConfig(rulePayload).then((res) => {
                    if (res?.data) onRuleConfigSaved?.(res.data);
                }).catch(async (err) => {
                    const errMsg = err?.response?.data?.message || err?.message || '';
                    if (errMsg.includes('Duplicate key') || errMsg.includes('EWS_Signal_Name_1') || err?.response?.status === 400) {
                        try {
                            const listResp = await getRuleConfigs();
                            const list = listResp?.data || listResp;
                            if (Array.isArray(list)) {
                                const found = list.find((doc: any) =>
                                    doc.EWS_Signal_Name && doc.EWS_Signal_Name.toLowerCase() === currentRuleDetails.signal.toLowerCase()
                                );
                                if (found?._id) {
                                    const updRes = await updateRuleConfig(found._id, rulePayload);
                                    if (updRes?.data) onRuleConfigSaved?.(updRes.data);
                                }
                            }
                        } catch (fErr) {
                            console.error('ConfigureRulesTab fallback update error:', fErr);
                        }
                    } else {
                        console.error('Save rule config error:', err);
                    }
                });
            }
        } catch (err: any) {
            console.error('Error saving rule config during test run:', err);
        }

        // 2. Call live Backend Test-Run API for Dry Run and sample execution records
        try {
            const rawEs = activeSavedRule?.baseEsQuery || ruleDetails?.baseEsQuery;
            let indexName = (rawEs && typeof rawEs === 'object' && rawEs.indexName) ? rawEs.indexName : 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_dev';
            indexName = typeof indexName === 'string' ? indexName.replace(/^'|'$/g, '').replace(/^"|"$/g, '').trim() : indexName;
            let query = (rawEs && typeof rawEs === 'object' && rawEs.query) ? rawEs.query : (typeof rawEs === 'string' ? rawEs : '{"query":{"match_all":{}}}');
            query = balanceJsonBrackets(query);
            const vars = (rawEs && typeof rawEs === 'object' && Array.isArray(rawEs.variables)) ? rawEs.variables : [];
            const keys = (rawEs && typeof rawEs === 'object' && Array.isArray(rawEs.keysToExtract)) ? rawEs.keysToExtract : [];
            const resolvedVars = ensureQueryVariables(query, vars, activeSavedRule?.variables || ruleDetails?.variables || []);

            const formattedFinalEsQuery = {
                indexName: indexName || 'logs-pepwise_data_telemetry_metrics_v0.3.0.2025.june-pepwise_dev',
                query,
                Variables: resolvedVars.map((v: any) => ({
                    variableName: v.variableName || v.variableIdentifier || v.key || '',
                    variableIdentifier: v.variableIdentifier || v.variableName || v.key || '',
                    value: v.value,
                    ...(v.operator ? { operator: v.operator } : {})
                })),
                keysToExtract: keys
            };

            const testPayload: any = {
                Domain: currentRuleDetails.domain || 'Volume',
                Component: currentRuleDetails.component || 'adf',
                Layer_Tool: currentRuleDetails.layer || 'bronze',
                Metric_KPI: currentRuleDetails.metricKPI || 'record count',
                EWS_Signal_Name: currentRuleDetails.signal || 'EWS.Rule',
                Detection_Logic_Trend_Pattern: currentRuleDetails.detectionLogic || '',
                Risk_Level: (currentRuleDetails.riskLevel || 'MEDIUM').toUpperCase(),
                Data_Source: currentRuleDetails.dataSource || 'ingestion metrics',
                Rule_Name: currentRuleDetails.ruleName || 'EWS Rule',
                Notes: currentRuleDetails.notes || '',
                Is_Deleted: false,
                Is_Active: false,
                Is_Approved: false,
                ruleConfigId: activeSavedRule?.backendId || ruleDetails?.backendId,
                _id: activeSavedRule?.backendId || ruleDetails?.backendId,
                Schedule: {
                    Eval_Window: form.evalWindow,
                    Interval: form.checkInterval,
                    Cooldown: form.coolDown
                },
                Resources: form.resources.map((resName) => {
                    const matched = dynamicResourceOptions.find((r) => r.value === resName);
                    return {
                        type: (matched?.resourceType || form.resourceType || 'ADF').toUpperCase(),
                        name: resName,
                        identifier: resName
                    };
                }),
                Final_ES_Query: formattedFinalEsQuery,
                Test_Run_Status: 'PASSED',
                Variables: []
            };

            const testResp = await runRuleTest(testPayload);
            if (testResp?.success) {
                const savedDoc = testResp?.data?.ruleConfig || {
                    _id: activeSavedRule?.backendId || ruleDetails?.backendId,
                    Resources: testPayload.Resources,
                    Schedule: testPayload.Schedule,
                    Test_Run_Status: testResp?.data?.Test_Run_Status || 'PASSED'
                };

                if (activeSavedRule) {
                    (activeSavedRule as any).Test_Run_Status = 'PASSED';
                    (activeSavedRule as any).testRunStatus = 'PASSED';
                }
                onRuleConfigSaved?.({
                    ...savedDoc,
                    Test_Run_Status: 'PASSED',
                    testRunStatus: 'PASSED'
                });

                const testResult = testResp?.data?.testResult || {};
                const testRunResults: any[] = Array.isArray(testResult.Test_Run_Result) ? testResult.Test_Run_Result : [];
                const totalHits = testResult.Test_Run_Count ?? testRunResults.length;
                const rawRecords: Record<string, any>[] = testRunResults.map((item: any) => ({
                    '@timestamp': item['@timestamp'],
                    ...(item['pepwise.body'] || {})
                }));

                const dynamicSamples: RuleTestSampleResult[] = rawRecords.slice(0, 10).map((src, idx) => {
                    const resourceName = src.resource_name || src.job_id || src.task_key || `Telemetry Record #${idx + 1}`;
                    const duration = src.execution_details?.duration_ms ? `${Math.round(src.execution_details.duration_ms / 1000)}s` : 'Normal';
                    return {
                        id: idx + 1,
                        pipelineName: String(resourceName),
                        resourceType: (form.resourceType || 'adf').toLowerCase(),
                        executionTimestamp: src['@timestamp'] ? new Date(src['@timestamp']).toLocaleString() : new Date().toLocaleString(),
                        metricValue: duration,
                        threshold: 'Evaluated Normal',
                        driftPercent: '0%',
                        status: 'PASS',
                        message: 'Telemetry point verified within threshold'
                    };
                });

                setSampleTestResults(dynamicSamples);
                setDryRunRawResults(rawRecords);
                setDryRunMessage(`Evaluated ${totalHits} telemetry sample(s) (Status: ${testResult.Test_Run_Status || 'PASSED'})`);
                setDryRunFailed(false);
                logAuditEntry(`Executed rule test via backend test runner across ${form.resources.length} configured resources.`);
            } else {
                const failureMsg = testResp?.message || 'Dry run evaluation returned unsuccessful status';
                setDryRunMessage(`Dry run execution failed: ${failureMsg}`);
                setDryRunFailed(true);
                setTestFailed(true);
                setTestErrorMessage(`Dry run error: ${failureMsg}`);
                setSampleTestResults([]);
                setDryRunRawResults([]);
            }
        } catch (err: any) {
            const apiMsg = err?.response?.data?.message || err?.message || 'Backend test runner service unavailable / not deployed';
            setDryRunMessage(`Dry run execution failed: ${apiMsg}`);
            setDryRunFailed(true);
            setTestFailed(true);
            setTestErrorMessage(`Dry run error: ${apiMsg}`);
            setSampleTestResults([]);
            setDryRunRawResults([]);
        } finally {
            setIsRunningTest(false);
        }
    };

    const handleDryRunClick = async () => {
        if (dryRunRawResults.length === 0) {
            await handleRunTest();
        }
        setShowDryRunTable(true);
    };

    const handleProceedToApproval = async () => {
        const targetBackendId = activeSavedRule?.backendId || ruleDetails?.backendId;
        if (targetBackendId) {
            try {
                const updateData: Partial<RuleConfigPayload> = {
                    Schedule: {
                        Eval_Window: form.evalWindow,
                        Interval: form.checkInterval,
                        Cooldown: form.coolDown
                    },
                    Resources: form.resources.map((resName) => {
                        const matched = dynamicResourceOptions.find((r) => r.value === resName);
                        return {
                            type: (matched?.resourceType || form.resourceType || 'ADF').toUpperCase(),
                            name: resName,
                            identifier: resName
                        };
                    }),
                    Test_Run_Status: 'PASSED'
                };
                const updResp = await updateRuleConfig(targetBackendId, updateData);
                if (updResp?.data) {
                    onRuleConfigSaved?.({
                        ...updResp.data,
                        Test_Run_Status: 'PASSED',
                        testRunStatus: 'PASSED'
                    });
                }
            } catch (err) {
                console.error('Error updating rule configuration on proceed:', err);
            }
        }
        onNavigateToTab?.(3);
    };

    const testsPassed = testExecuted && !testFailed && !dryRunFailed && isScheduleValid && form.resources.length > 0;
    const validationPassed = isScheduleValid && testsPassed;
    const canProceed = (validationPassed || (overrideEnabled && isAuthorizedToOverride)) && form.resources.length > 0;

    useEffect(() => {
        onCanProceedChange?.(canProceed);
    }, [canProceed, onCanProceedChange]);

    const handleOverrideToggle = (checked: boolean) => {
        setOverrideEnabled(checked);
        logAuditEntry(checked ? 'Authorized override enabled to bypass validation' : 'Authorized override disabled');
    };

    return (
        <Box sx={{ mt: 3 }}>
            {/* Header Description */}
            <Box sx={{ mb: 2.5 }}>
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 700, color: '#39ACE7', mb: 0.5 }}>
                    Configure Rules
                </Typography>
                <Typography sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                    Apply Business Domain, Sector & Program scope. Set scheduling and conditions, then submit for approval
                </Typography>
            </Box>

            {/* Top Outlined Summary Card */}
            <Box
                sx={{
                    border: '1.5px solid #39ACE7',
                    borderRadius: '8px',
                    backgroundColor: '#f4f9fd',
                    p: 2.5,
                    mb: 3.5,
                    boxShadow: '0 2px 6px rgba(0, 55, 113, 0.06)'
                }}
            >
                {/* Card Top Row: Rule Name (or Dropdown selector if multiple) + Status + Risk Badge + Change Rule */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        mb: 2
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        {savedRules.length > 1 ? (
                            <Box sx={{ minWidth: 220, maxWidth: 320 }}>
                                <StyledSelect
                                    label="Selected Rule"
                                    hideLabel
                                    value={String(currentSelectedId || activeSavedRule?.id || '')}
                                    onChange={handleSelectRule}
                                    options={savedRules.map((r) => ({
                                        value: String(r.id),
                                        label: r.ruleName
                                    }))}
                                    height={34}
                                />
                            </Box>
                        ) : (
                            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#39ACE7' }}>
                                {displayedRuleName}
                            </Typography>
                        )}

                        <Box
                            sx={{
                                border: '1px solid #94a3b8',
                                color: '#64748b',
                                backgroundColor: '#fff',
                                px: 1.2,
                                py: 0.2,
                                borderRadius: '12px',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                textTransform: 'lowercase'
                            }}
                        >
                            {displayedStatus.toLowerCase()}
                        </Box>

                        <Box
                            sx={{
                                backgroundColor: getRiskColor(displayedRiskLevel),
                                color: '#fff',
                                px: 1.5,
                                py: 0.2,
                                borderRadius: '12px',
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                textTransform: 'uppercase'
                            }}
                        >
                            {displayedRiskLevel}
                        </Box>
                    </Box>

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            const targetRuleId = currentSelectedId || activeSavedRule?.id || selectedRuleId;
                            if (onChangeRule && targetRuleId != null) {
                                onChangeRule(targetRuleId);
                            } else if (onNavigateToTab) {
                                onNavigateToTab(0);
                            }
                        }}
                        endIcon={<EditIcon sx={{ fontSize: '0.95rem !important' }} />}
                        sx={{
                            borderColor: '#39ACE7',
                            color: '#39ACE7',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 1.75,
                            py: 0.4,
                            borderRadius: '6px',
                            backgroundColor: '#fff',
                            '&:hover': {
                                borderColor: '#2c8ebb',
                                backgroundColor: '#f0f9ff'
                            }
                        }}
                    >
                        Change rule
                    </Button>
                </Box>

                {/* Card Bottom Row: 3 Columns (Signal, Domain path, Handler) */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1.5fr 1fr' },
                        gap: 2,
                        pt: 1.5,
                        borderTop: '1px solid #e0edf7'
                    }}
                >
                    <Box>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                            Signal
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                            {displayedSignal}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                            Domain path
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                            {displayedDomainPath}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mb: 0.5 }}>
                            Handler
                        </Typography>
                        <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                            {displayedHandler}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* 1. APPLY SCOPE */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="APPLY SCOPE" />
                <Box sx={{ mt: 1 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 0.75 }}>
                        Resources — select resources to apply this rule to
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 3fr', gap: 2 }}>
                        <StyledSelect
                            label="Resource Type"
                            value={form.resourceType || (dynamicResourceTypeOptions[0]?.value || 'adf')}
                            onChange={(val) => handleFormChange('resourceType', val)}
                            options={dynamicResourceTypeOptions}
                        />
                        <Autocomplete
                            multiple
                            size="small"
                            fullWidth
                            loading={isLoadingResources}
                            options={availableResourceOptions}
                            value={selectedResourceObjects}
                            getOptionLabel={(option: ResourceOption) => option.label || option.value}
                            isOptionEqualToValue={(opt, val) => opt.value === val.value}
                            onChange={(_e: any, newValue: ResourceOption[]) => {
                                setForm((prev) => ({
                                    ...prev,
                                    resources: newValue.map((item) => item.value)
                                }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    size="small"
                                    label="Resources"
                                    placeholder={isLoadingResources ? 'Loading resources...' : (form.resources.length === 0 ? 'Select a Resource' : '')}
                                    InputLabelProps={{ shrink: true, sx: labelStyle }}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {isLoadingResources ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: '#fff',
                                            minHeight: '40px',
                                            ...inputBorderStyles
                                        },
                                        '& .MuiInputBase-input': { color: '#000 !important' }
                                    }}
                                />
                            )}
                            renderTags={(tagValue: ResourceOption[], getTagProps) =>
                                tagValue.map((option, index) => {
                                    const { key, ...tagProps } = getTagProps({ index });
                                    return (
                                        <Chip
                                            key={key}
                                            label={option.label || option.value}
                                            size="small"
                                            deleteIcon={<CloseIcon sx={{ fontSize: '14px !important', color: '#0B4F73 !important' }} />}
                                            {...tagProps}
                                            sx={{
                                                backgroundColor: 'rgba(57, 172, 231, 0.16) !important',
                                                color: '#0B4F73 !important',
                                                border: '1px solid rgba(57, 172, 231, 0.45) !important',
                                                fontWeight: 600,
                                                fontSize: '0.75rem',
                                                height: '24px',
                                                borderRadius: '16px',
                                                px: '6px',
                                                margin: '2px !important',
                                                '& .MuiChip-label': {
                                                    color: '#0B4F73 !important',
                                                    padding: '0 4px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                },
                                                '& .MuiChip-deleteIcon': {
                                                    margin: 0,
                                                    color: '#0B4F73 !important',
                                                    '&:hover': { color: '#003771 !important' }
                                                }
                                            }}
                                        />
                                    );
                                })
                            }
                            PaperComponent={({ children, ...paperProps }) => (
                                <Paper {...paperProps} sx={{ ...ewsAutocompletePaperSx, zIndex: 1300 }}>
                                    {children}
                                </Paper>
                            )}
                        />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.75 }}>
                        {form.resources.length} resources selected
                    </Typography>
                </Box>
            </Box>

            {/* 2. SCHEDULING & CONDITIONS */}
            <Box sx={{ mb: 4 }}>
                <SectionHeader title="SCHEDULING & CONDITIONS" />
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2.5, mt: 1.5 }}>
                    {/* Eval window */}
                    <Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Box sx={{ flex: 1.4 }}>
                                <StyledTextField
                                    label="Eval window"
                                    required
                                    placeholder="14"
                                    inputProps={{ type: 'number', min: 1, step: 1 }}
                                    value={evalParsed.num}
                                    onChange={(e) => handleScheduleNumChange('evalWindow', e.target.value)}
                                    onBlur={() => handleScheduleBlur('evalWindow')}
                                    error={scheduleTouched.evalWindow && !!scheduleErrors.evalWindow}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <StyledSelect
                                    label="Unit"
                                    value={evalParsed.unit}
                                    options={SCHEDULE_UNIT_OPTIONS}
                                    onChange={(val) => handleScheduleUnitChange('evalWindow', val)}
                                />
                            </Box>
                        </Box>
                        {scheduleTouched.evalWindow && scheduleErrors.evalWindow ? (
                            <Typography sx={{ fontSize: '0.75rem', color: '#d32f2f', mt: 0.5, ml: 0.5 }}>
                                {scheduleErrors.evalWindow}
                            </Typography>
                        ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.5, ml: 0.5 }}>
                                Evaluation lookback window (e.g. 14d)
                            </Typography>
                        )}
                    </Box>

                    {/* Check interval */}
                    <Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Box sx={{ flex: 1.4 }}>
                                <StyledTextField
                                    label="Check interval"
                                    required
                                    placeholder="1"
                                    inputProps={{ type: 'number', min: 1, step: 1 }}
                                    value={checkParsed.num}
                                    onChange={(e) => handleScheduleNumChange('checkInterval', e.target.value)}
                                    onBlur={() => handleScheduleBlur('checkInterval')}
                                    error={scheduleTouched.checkInterval && !!scheduleErrors.checkInterval}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <StyledSelect
                                    label="Unit"
                                    value={checkParsed.unit}
                                    options={SCHEDULE_UNIT_OPTIONS}
                                    onChange={(val) => handleScheduleUnitChange('checkInterval', val)}
                                />
                            </Box>
                        </Box>
                        {scheduleTouched.checkInterval && scheduleErrors.checkInterval ? (
                            <Typography sx={{ fontSize: '0.75rem', color: '#d32f2f', mt: 0.5, ml: 0.5 }}>
                                {scheduleErrors.checkInterval}
                            </Typography>
                        ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.5, ml: 0.5 }}>
                                Rule check frequency (e.g. 1h)
                            </Typography>
                        )}
                    </Box>

                    {/* Cool-down */}
                    <Box>
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <Box sx={{ flex: 1.4 }}>
                                <StyledTextField
                                    label="Cool-down"
                                    required
                                    placeholder="30"
                                    inputProps={{ type: 'number', min: 1, step: 1 }}
                                    value={coolDownParsed.num}
                                    onChange={(e) => handleScheduleNumChange('coolDown', e.target.value)}
                                    onBlur={() => handleScheduleBlur('coolDown')}
                                    error={scheduleTouched.coolDown && !!scheduleErrors.coolDown}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <StyledSelect
                                    label="Unit"
                                    value={coolDownParsed.unit}
                                    options={SCHEDULE_UNIT_OPTIONS}
                                    onChange={(val) => handleScheduleUnitChange('coolDown', val)}
                                />
                            </Box>
                        </Box>
                        {scheduleTouched.coolDown && scheduleErrors.coolDown ? (
                            <Typography sx={{ fontSize: '0.75rem', color: '#d32f2f', mt: 0.5, ml: 0.5 }}>
                                {scheduleErrors.coolDown}
                            </Typography>
                        ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.5, ml: 0.5 }}>
                                Alert suppression duration (e.g. 30m)
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* 3. RULE TEST EXECUTION */}
            <Box sx={{ mb: 3 }}>
                <Box sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                    overflow: 'hidden',
                    mb: 2.5
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        borderBottom: '1px solid #e2e8f0',
                        backgroundColor: '#fafbfc'
                    }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#39ACE7', letterSpacing: '0.5px' }}>
                            RULE TEST
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {testExecuted && !testFailed && !dryRunFailed && (
                                <Box sx={{
                                    backgroundColor: '#d4f8d3',
                                    color: '#1b802e',
                                    px: 1.5,
                                    py: 0.25,
                                    borderRadius: '12px',
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    textTransform: 'lowercase'
                                }}>
                                    passed
                                </Box>
                            )}
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleRunTest}
                                disabled={isRunningTest}
                                startIcon={isRunningTest ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : (testExecuted ? <ReplayIcon sx={{ fontSize: '1rem' }} /> : <PlayArrowIcon sx={{ fontSize: '1rem' }} />)}
                                sx={{
                                    backgroundColor: '#39ACE7',
                                    color: '#fff',
                                    fontWeight: 600,
                                    fontSize: '0.8rem',
                                    textTransform: 'none',
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: '4px',
                                    '&:hover': { backgroundColor: '#39ACE7' }
                                }}
                            >
                                {isRunningTest ? 'Running Test...' : (testExecuted ? 'Re-run Test' : 'Run Test')}
                            </Button>
                        </Box>
                    </Box>

                    {testFailed && testErrorMessage && (
                        <Alert severity="error" sx={{ m: 2, mb: 0 }}>{testErrorMessage}</Alert>
                    )}

                    {isRunningTest && (
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <LoaderComponent size={32} />
                        </Box>
                    )}

                    {/* 8 Rule Test Checklist Items */}
                    <Box sx={{ p: 0.5 }}>
                        <RuleTestItemRow
                            title="Signal validation"
                            description={currentRuleDetails.signal ? `Found in EWS catalog (${currentRuleDetails.signal})` : 'No EWS signal selected'}
                            status={testExecuted ? (currentRuleDetails.signal ? 'pass' : 'fail') : 'pending'}
                        />
                        <RuleTestItemRow
                            title="Detection logic"
                            description={currentRuleDetails.detectionLogic ? 'Logic defined' : 'No detection logic defined'}
                            status={testExecuted ? (currentRuleDetails.detectionLogic ? 'pass' : 'fail') : 'pending'}
                        />
                        <RuleTestItemRow
                            title="Conditions check"
                            description={`${conditionsCount} condition(s) defined`}
                            status={testExecuted ? (conditionsCount > 0 ? 'pass' : 'fail') : 'pending'}
                        />
                        <RuleTestItemRow
                            title="Data source connectivity"
                            description={`Source: ${currentRuleDetails.dataSource || 'SLA timestamps'}`}
                            status={testExecuted ? (currentRuleDetails.dataSource ? 'pass' : 'fail') : 'pending'}
                        />
                        <RuleTestItemRow
                            title="Handler availability"
                            description={`Handler: ${displayedHandler && displayedHandler !== '—' ? displayedHandler : 'sku_margin_tracker.py'}`}
                            status={testExecuted ? (displayedHandler && displayedHandler !== '—' ? 'pass' : 'warning') : 'pending'}
                        />
                        <RuleTestItemRow
                            title="Scheduling parameters"
                            description={isScheduleValid ? `Eval: ${form.evalWindow} · Interval: ${form.checkInterval} · Cooldown: ${form.coolDown}` : (scheduleErrors.evalWindow || scheduleErrors.checkInterval || scheduleErrors.coolDown || 'Invalid schedule parameter')}
                            status={testExecuted ? (isScheduleValid ? 'pass' : 'fail') : 'pending'}
                        />
                        <RuleTestItemRow
                            title="Scope assignment"
                            description={form.resources.length > 0 ? `${form.resources.length} resource(s) assigned` : 'No scope — applies globally'}
                            status={testExecuted ? (form.resources.length > 0 ? 'pass' : 'warning') : 'pending'}
                        />
                        <RuleTestItemRow
                            title="Dry run"
                            description={dryRunMessage || (testExecuted ? 'No anomalies in last 24h' : 'Pending test execution — click to run')}
                            status={testExecuted ? (dryRunFailed ? 'fail' : 'pass') : 'pending'}
                            onClick={handleDryRunClick}
                        />
                    </Box>
                </Box>

                {/* Checkbox: Share test results with approvers */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={shareWithApprovers}
                                onChange={(e) => setShareWithApprovers(e.target.checked)}
                                size="small"
                                sx={{ color: '#39ACE7', '&.Mui-checked': { color: '#39ACE7' }, p: 0.5 }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
                                Share test results with approvers
                            </Typography>
                        }
                    />
                </Box>

            </Box>

            {showDryRunTable && dryRunRawResults.length > 0 && (
                <Box sx={{ mb: 3 }}>
                    <PaginatedResultsTable
                        tableType="ews-dry-run-results"
                        data={dryRunRawResults}
                        isShowMoreButton={false}
                        onClose={() => setShowDryRunTable(false)}
                    />
                </Box>
            )}
            {showDryRunTable && dryRunRawResults.length === 0 && (
                <Typography sx={{ fontSize: '0.875rem', color: '#64748b', py: 2, textAlign: 'center' }}>
                    No results returned for this dry run.
                </Typography>
            )}

            {/* Authorized Admin Override */}
            {isAuthorizedToOverride && (
                <Box sx={{ mt: 2, mb: 3, p: 2, backgroundColor: '#f8fafc', borderRadius: 1, border: '1px solid #e2e8f0' }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={overrideEnabled}
                                onChange={(e) => handleOverrideToggle(e.target.checked)}
                                sx={{ color: '#39ACE7', '&.Mui-checked': { color: '#39ACE7' } }}
                            />
                        }
                        label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LockIcon sx={{ fontSize: '1rem', color: '#f57f17' }} />
                                <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>
                                    Authorized Admin Override (Enable to advance despite pending validation warnings)
                                </Typography>
                            </Box>
                        }
                    />
                </Box>
            )}

            {/* Bottom Navigation */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                pt: 2.5,
                borderTop: '1px solid #e2e8f0',
                mt: 3,
                mb: 4
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '0.85rem', color: '#64748b' }}>
                        Configuring :
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#39ACE7' }}>
                        Rule Test
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={() => onNavigateToTab ? onNavigateToTab(1) : onNavigateToServiceMapping?.()}
                        startIcon={<ArrowBackIcon sx={{ fontSize: '1rem' }} />}
                        sx={{
                            borderColor: '#cbd5e1',
                            color: '#475569',
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 2.5
                        }}
                    >
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleProceedToApproval}
                        disabled={!canProceed}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem' }} />}
                        sx={{
                            backgroundColor: '#39ACE7',
                            color: '#fff',
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 2.5,
                            '&:hover': { backgroundColor: '#39ACE7' },
                            '&.Mui-disabled': { backgroundColor: '#94a3b8', color: '#fff' }
                        }}
                    >
                        Proceed to Approval
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default ConfigureRulesTab;
